import { useState } from "react";
import { Link } from "react-router-dom";
import { Printer, BookOpen } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const MODES = [{ k: "browser", l: "Browser" }, { k: "network", l: "Termica (IP)" }, { k: "network_pdf", l: "Ufficio PDF (IP)" }, { k: "usb", l: "Termica USB" }];
const HINTS = {
  browser: "Stampa tramite finestra di stampa del browser.",
  network: "Il server invia i comandi ESC/POS alla stampante termica di rete (porta 9100) e stampa automaticamente alla vincita.",
  network_pdf: "Per stampanti da ufficio (es. Ricoh, HP, Brother): il server invia il PDF della ricevuta su A4 alla stampante (porta 9100, PDF Direct Print) e stampa automaticamente alla vincita.",
  usb: "Stampante USB collegata al totem tramite WebUSB (Chrome/Edge). Alla prima vincita va autorizzata una volta.",
};
const isNetwork = (m) => m === "network" || m === "network_pdf";
const isThermal = (m) => m === "network" || m === "usb";

export function PrinterSection({ form, set }) {
  const [testing, setTesting] = useState(false);
  const testPrinter = async () => {
    if (!form.printer_ip) return toast.error("Inserisci l'indirizzo IP della stampante");
    setTesting(true);
    try {
      await api.printerTest(form.printer_ip.trim(), Number(form.printer_port) || 9100, Number(form.printer_width) || 80, form.printer_mode === "network_pdf" ? "pdf" : "escpos");
      toast.success("Stampa di prova inviata: controlla la stampante");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stampante non raggiungibile");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg bg-secondary/40 p-4 space-y-3">
      <Label>Stampante ricevute totem</Label>
      <div className="grid grid-cols-2 gap-2">
        {MODES.map((o) => (
          <Button key={o.k} type="button" size="sm" data-testid={`printer-mode-${o.k}`} variant={form.printer_mode === o.k ? "default" : "secondary"} onClick={() => set("printer_mode", o.k)}>
            {o.l}
          </Button>
        ))}
      </div>
      {isThermal(form.printer_mode) && (
        <div className="space-y-1">
          <Label className="text-xs">Larghezza carta</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="printer-width-selector">
            {[[80, "80 mm · 48 colonne"], [58, "58 mm · 32 colonne"]].map(([w, l]) => (
              <Button key={w} type="button" size="sm" data-testid={`printer-width-${w}`} variant={Number(form.printer_width) === w ? "default" : "secondary"} onClick={() => set("printer_width", w)}>
                {l}
              </Button>
            ))}
          </div>
        </div>
      )}
      {isNetwork(form.printer_mode) && (
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2 space-y-1">
            <Label className="text-xs">Indirizzo IP stampante</Label>
            <Input data-testid="printer-ip-input" value={form.printer_ip} onChange={(e) => set("printer_ip", e.target.value)} placeholder="192.168.1.50" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Porta</Label>
            <Input data-testid="printer-port-input" type="number" value={form.printer_port} onChange={(e) => set("printer_port", e.target.value)} />
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="printer-test-button" onClick={testPrinter} disabled={testing} className="col-span-3 gap-2">
            <Printer className="h-3.5 w-3.5" /> {testing ? "Invio…" : "Stampa di prova"}
          </Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">{HINTS[form.printer_mode]}</p>
      {form.printer_mode === "browser" && (
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border/50">
          <div>
            <Label className="text-xs">Stampa automatica alla vincita</Label>
            <p className="text-[11px] text-muted-foreground">Con Chrome in modalità <code>--kiosk-printing</code> la ricevuta esce da sola sulla stampante predefinita del totem.</p>
          </div>
          <Switch data-testid="auto-print-switch" checked={!!form.auto_print} onCheckedChange={(v) => set("auto_print", v)} />
        </div>
      )}
      {isNetwork(form.printer_mode) && (
        <p className="text-[11px] rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-300" data-testid="agent-required-note">
          Il server è nel cloud e non raggiunge la rete del negozio: serve l'<b>Agente di stampa</b> su un PC in negozio. La "Stampa di prova" qui sopra funziona solo tramite l'agente.
        </p>
      )}
      <Link to="/guida-stampa" target="_blank" className="inline-flex items-center gap-1.5 text-xs text-primary underline" data-testid="print-guide-link">
        <BookOpen className="h-3.5 w-3.5" /> Guida alla configurazione della stampa
      </Link>
    </div>
  );
}
